import { isNil, isString, map, slice } from './lodash';
import { dayjs, DayjsType, dayjsUtils } from './dayjs';
import { Dayjs } from 'dayjs';
import { Region, TIMEZONE_KEY } from '../interfaces';
import { isNumeric } from './common';
import { DEPT_LEVEL_MAX } from '../constants';

/**
 * 字符串转化成数字
 */
export const str2num = (value: string) => {
    if (isNil(value)) {
        return null;
    } else {
        const number = parseFloat(value);
        if (isNaN(number)) {
            return null;
        }
        return number;
    }
};

/**
 * 转化为数字类型
 */
export const parseNumber = (value: any) => {
    const number = parseFloat(value);
    if (!isNaN(number) && isFinite(number)) {
        return number;
    }
    return null;
};

/**
 * 获取合法日期对象（invalid date 返回 null)
 *
 
 */
export const getValidDate = (value: any, timezone?: TIMEZONE_KEY): Dayjs | null => {
    return dayjs(new Date(value)).isValid() && dayjs(value).isValid()
        ? dayjsUtils.dayjs(value, timezone)
        : null;
};

/**
 * 日期时间字符串等转日期
 *
 * @param value yyyy-MM-dd
 */
export const parseDate = (value: any, timezone?: TIMEZONE_KEY) => {
    if (isNil(value)) {
        return null;
    }
    if (isNumeric(value)) {
        return getValidDate(value, timezone);
    }
    if (isString(value)) {
        if (value.match(/^[\d-]+T[\d:.]+Z$/)) {
            return getValidDate(value, timezone);
        }
        const match = value.trim().split(' ');
        if (match) {
            const dateMatch: any = match[0] && match[0].split('-');
            const year = dateMatch[0];
            if (!isNumeric(year)) {
                return null;
            }
            const month = isNumeric(dateMatch[1]) ? dateMatch[1] - 1 : 0;
            const day = isNumeric(dateMatch[2]) ? dateMatch[2] : 1;
            if (match[1]) {
                const timeMatch = match[1].split(':');
                const hour: any = isNumeric(timeMatch[0]) ? timeMatch[0] : 0;
                const minute: any = isNumeric(timeMatch[1]) ? timeMatch[1] : 0;
                let second: any = isNumeric(timeMatch[2]) ? timeMatch[2] : 0;
                let millisecond: any = 0;
                if (/\./.test(timeMatch[2]!)) {
                    const secondMatch = timeMatch[2]?.split('.') || [];
                    second = isNumeric(secondMatch[0]) ? secondMatch[0] : 0;
                    millisecond = isNumeric(secondMatch[1]) ? secondMatch[1] : 0;
                }
                return dayjsUtils.getRealTimeZoneDayjs(
                    new Date(year, month, day, hour, minute, second, millisecond),
                    timezone
                );
            } else {
                return dayjsUtils.getRealTimeZoneDayjs(new Date(year, month, day), timezone);
            }
        } else {
            return null;
        }
    }
    return getValidDate(value, timezone);
};

/**
 * 星期 (不支持国际化)
 */
const _DN = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六'];

/**
 * 星期(缩写)(不支持国际化)
 */
const _SDN = ['日', '一', '二', '三', '四', '五', '六'];

/**
 * 月份(不支持国际化)
 */
const _MN = [
    '一月',
    '二月',
    '三月',
    '四月',
    '五月',
    '六月',
    '七月',
    '八月',
    '九月',
    '十月',
    '十一月',
    '十二月'
];

/**
 * 让字符串通过指定字符做补齐的函数
 *
 * @static
 * @param {string} text 原始字符串
 * @param {number} size 总共需要的位数
 * @param {string} ch 用于补齐的字符
 * @returns {string}  补齐后的字符串
 */
export const leftPad = (text: string, size: number, ch = ' ') => {
    let result = String(text);
    while (result.length < size) {
        result = ch + result;
    }
    return result.toString();
};

/**
 * 日期格式化转换
 *
 * @param format
 * @param date
 * @param onCustomFormat
 */
const compileDateFormat = (
    format: any,
    date: Dayjs,
    onCustomFormat?: (format: any, date: Dayjs) => string
) => {
    let str = format.str;
    const len = format.len;
    const ch = format['char'];
    let hour = dayjsUtils.getHour(date) % 12;
    switch (ch) {
        case 'E': // 星期
            if (len > 2) {
                str = _DN[dayjsUtils.getDay(date)];
            } else if (len > 1) {
                str = _SDN[dayjsUtils.getDay(date)];
            } else {
                str = String(dayjsUtils.getDay(date));
            }
            break;
        case 'y': // 年
            if (len <= 3) {
                str = String(dayjsUtils.getYear(date)).slice(2, 4);
            } else {
                str = dayjsUtils.getYear(date);
            }
            break;
        case 'M': // 月
            if (len > 2) {
                str = _MN[dayjsUtils.getMonth(date)];
            } else if (len < 2) {
                str = dayjsUtils.getMonth(date) + 1;
            } else {
                str = leftPad(String(dayjsUtils.getMonth(date) + 1), 2, '0');
            }
            break;
        case 'd': // 日
            if (len > 1) {
                str = leftPad(String(dayjsUtils.getDate(date)), 2, '0');
            } else {
                str = dayjsUtils.getDate(date);
            }
            break;
        case 'h': // 时(12)
            if (hour === 0) {
                hour = 12;
            }
            if (len > 1) {
                str = leftPad(String(hour), 2, '0');
            } else {
                str = hour;
            }
            break;
        case 'H': // 时(24)
            if (len > 1) {
                str = leftPad(String(dayjsUtils.getHour(date)), 2, '0');
            } else {
                str = dayjsUtils.getHour(date);
            }
            break;
        case 'm':
            if (len > 1) {
                str = leftPad(String(dayjsUtils.getMinute(date)), 2, '0');
            } else {
                str = dayjsUtils.getMinute(date);
            }
            break;
        case 's':
            if (len > 1) {
                str = leftPad(String(dayjsUtils.getSecond(date)), 2, '0');
            } else {
                str = dayjsUtils.getSecond(date);
            }
            break;
        case 'S':
            if (len > 1) {
                str = leftPad(String(dayjsUtils.getMillisecond(date)), 3, '0');
            } else {
                str = dayjsUtils.getMillisecond(date);
            }
            break;
        default:
            if (onCustomFormat) {
                str = onCustomFormat(format, date);
            } else {
                str = format.str;
            }
            break;
    }
    return str;
};

/**
 * 日期转文本
 *
 * @param date
 * @param format
 * @param onCustomFormat
 */
export const date2Str = (
    date: any,
    format: any,
    onCustomFormat?: (format: any, date: any) => string
) => {
    if (!date) {
        return '';
    }
    // O(len(format))
    format = format || 'yyyy-MM-dd';
    const len = format.length;
    let result = '';
    if (len > 0) {
        let flagch = format.charAt(0);
        let start = 0;
        let str = flagch;
        for (let i = 1; i < len; i++) {
            const ch = format.charAt(i);
            if (flagch !== ch) {
                result += compileDateFormat(
                    {
                        char: flagch,
                        str: str,
                        len: i - start
                    },
                    date,
                    onCustomFormat
                );
                flagch = ch;
                start = i;
                str = flagch;
            } else {
                str += ch;
            }
        }
        result += compileDateFormat(
            {
                char: flagch,
                str: str,
                len: len - start
            },
            date,
            onCustomFormat
        );
    }
    return result;
};

export const formatNumberWithSep = (
    value: any,
    sep?: {
        thousand?: string;
        decimal?: string;
    }
) => {
    const { thousand = '', decimal = '.' } = sep || {};
    if (thousand === '' && decimal === '.') {
        // 若是默认分隔符，则返回原值
        return value;
    }
    if (!isNumeric(value)) {
        // 若非数字，则返回原值
        return value;
    }
    // 转为字符串，防止精度丢失（如123.400）
    const strValue = String(value);
    // 判断是否为负数
    const isNegative = Number(strValue) < 0;
    // 判断是否有小数点
    const hasDecimalSep = strValue.includes('.');
    // 分割整数部分和小数部分
    const [integerPart, decimalPart] = strValue.split('.');
    // 整数部分去掉正负号
    const strInteger = String(Math.abs(Number(integerPart)));
    // 整数部分添加千分位分隔符
    const integer =
        thousand === '' ? strInteger : strInteger.replace(/\B(?=(\d{3})+(?!\d))/g, thousand);
    // 组装并返回
    return `${isNegative ? '-' : ''}${integer}${hasDecimalSep ? decimal : ''}${decimalPart ?? ''}`;
};

/**
 * yyyy-MM-dd => YYYY-MM-DD
 * @param format
 */
export const transformDayjsFormat = (format: string) => {
    return format.replace(/y/g, 'Y').replace(/d/g, 'D');
};

/**
 * YYYY-MM-DD => yyyy-MM-dd
 * @param format
 */
export const transformNormalFormat = (format: string) => {
    return format.replace(/Y/g, 'y').replace(/D/g, 'd');
};

export const formatRegionDatetime = (props: {
    value?: DayjsType | null;
    region: Region;
    formatType: keyof Region['dateTimeFormat'];
}) => {
    const { value, region, formatType } = props;
    return isNil(value) ? '' : value.format(region.dateTimeFormat[formatType]);
};

/**
 * 部门最大层级统一为10层
 * 成员卡片包含当前部门；部门卡片不包含当前部门
 *
 * @param props
 */
export const formatDeptPath = (props: {
    hasCurrentDept?: boolean;
    deptPath: { name: string }[];
    corpName: string;
}) => {
    const { hasCurrentDept, deptPath, corpName } = props;
    const showDeptPath = hasCurrentDept
        ? slice(deptPath, -DEPT_LEVEL_MAX)
        : slice(deptPath, -1 - DEPT_LEVEL_MAX, -1);
    const deptPathStr = map(showDeptPath, 'name').join('/');
    return deptPathStr || corpName;
};
